import type { BillingEntryDto, BillingRefundOutcome } from '@lib/dto/billing.dto'
import type { BillingPurchaseStatus } from '@lib/enums/billing-purchase-status.enum'
import { cardBrandLabel } from '@lib/helpers/card-brand'
import { formatCents, formatMicros } from '@lib/helpers/money'
import { RecordFacts, RecordHeadline, RecordSection } from '@/components/RecordSheet'
import { Badge, type BadgeTone } from '@/components/ui/badge'

type CreditEntry = Extract<BillingEntryDto, { type: 'top_up' | 'auto_reload' }>
type EntryPurchase = NonNullable<CreditEntry['purchase']>
type RefundEntry = Extract<BillingEntryDto, { type: 'refund' | 'closure_refund' }>

const PURCHASE_STATUS: Record<BillingPurchaseStatus, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Pending', tone: 'neutral' },
  succeeded: { label: 'Paid', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  expired: { label: 'Expired', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
}

export const REFUND_STATUS: Record<BillingRefundOutcome, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Pending', tone: 'info' },
  succeeded: { label: 'Refunded', tone: 'success' },
  needs_attention: { label: 'Needs attention', tone: 'warning' },
}

function centsOrDash(cents: number | null): string {
  return cents === null ? '—' : formatCents(cents)
}

/** When the entry was made, and who or what made it. */
function Provenance({ entry, by }: { entry: BillingEntryDto; by: string }) {
  return (
    <>
      <dt className="text-muted-foreground">Date</dt>
      <dd>{new Date(entry.createdAt).toLocaleString()}</dd>
      <dt className="text-muted-foreground">{by}</dt>
      <dd className="truncate">{entry.app ? `${entry.actor.name} via ${entry.app}` : entry.actor.name}</dd>
    </>
  )
}

function PurchaseDetail({ entry, purchase }: { entry: CreditEntry; purchase: EntryPurchase }) {
  const status = PURCHASE_STATUS[purchase.status]
  return (
    <RecordHeadline
      facts={
        <RecordFacts>
          <dt className="text-muted-foreground">Credit</dt>
          <dd className="tabular-nums">{formatCents(purchase.creditCents)}</dd>
          <dt className="text-muted-foreground">Sales tax</dt>
          <dd className="tabular-nums">{centsOrDash(purchase.taxCents)}</dd>
          <dt className="text-muted-foreground">Total paid</dt>
          <dd className="tabular-nums">{centsOrDash(purchase.totalCents)}</dd>
          <dt className="text-muted-foreground">Card</dt>
          <dd>{purchase.card ? `${cardBrandLabel(purchase.card.brand)} •••• ${purchase.card.last4}` : '—'}</dd>
          <Provenance entry={entry} by={entry.type === 'auto_reload' ? 'Set up by' : 'Bought by'} />
        </RecordFacts>
      }
    >
      <h3 className="text-body-lg font-semibold">{entry.type === 'auto_reload' ? 'Auto-reload' : 'Credit purchase'}</h3>
      <Badge tone={status.tone}>{status.label}</Badge>
    </RecordHeadline>
  )
}

function RefundDetail({ entry, refund }: { entry: RefundEntry; refund: RefundEntry['refund'] }) {
  const status = REFUND_STATUS[refund.status]
  const closing = entry.type === 'closure_refund'
  return (
    <>
      <RecordHeadline
        facts={
          <RecordFacts>
            <dt className="text-muted-foreground">Credit</dt>
            <dd className="tabular-nums">{formatMicros(-entry.amountMicros)}</dd>
            <dt className="text-muted-foreground">Fee</dt>
            <dd className="tabular-nums">{formatCents(refund.feeCents)}</dd>
            <dt className="text-muted-foreground">Returned</dt>
            <dd className="tabular-nums">{formatCents(refund.netCents)}</dd>
            <dt className="text-muted-foreground">Tax returned</dt>
            <dd className="tabular-nums">{formatCents(refund.taxCents)}</dd>
            <Provenance entry={entry} by={closing ? 'By' : 'Refunded by'} />
          </RecordFacts>
        }
      >
        <h3 className="text-body-lg font-semibold">{closing ? 'Refund on closing' : 'Refund'}</h3>
        <Badge tone={status.tone}>{status.label}</Badge>
      </RecordHeadline>
      {refund.status === 'needs_attention' ? (
        <RecordSection title="Needs attention">
          <p className="text-body">PixelSign will contact you about part of this refund.</p>
        </RecordSection>
      ) : null}
      <RecordSection title="Where it goes">
        <p className="text-body">
          Back to the card payments the credit was bought with, newest first. Cards usually show a refund within 5–10
          business days.
        </p>
      </RecordSection>
    </>
  )
}

/** Whether a history entry has more to show than its row: a purchase's payment, or a refund's breakdown. */
export function hasEntryDetail(entry: BillingEntryDto): boolean {
  switch (entry.type) {
    case 'top_up':
    case 'auto_reload':
      return entry.purchase !== null
    case 'refund':
    case 'closure_refund':
      return true
    default:
      return false
  }
}

/** A history entry's details in the record tray. */
export function BillingEntryDetail({ entry }: { entry: BillingEntryDto }) {
  switch (entry.type) {
    case 'top_up':
    case 'auto_reload':
      return entry.purchase ? <PurchaseDetail entry={entry} purchase={entry.purchase} /> : null
    case 'refund':
    case 'closure_refund':
      return <RefundDetail entry={entry} refund={entry.refund} />
    default:
      return null
  }
}
